import { Fragment, useMemo, useState } from 'react';
import { ArrowDownTrayIcon, ChevronDownIcon, ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { DEMO_PRODUCTS, DEMO_STATIONS } from '../../../data/perception-demo-data';
import { formatDemoPercent, formatDemoTimestamp } from '../../../services/dashboard/perception-demo-service';
import { DemoCard, DemoStatus } from './PerceptionDemoUI';

const productNames = Object.fromEntries(DEMO_PRODUCTS.map(({ id, name }) => [id, name]));
const stationNames = Object.fromEntries(DEMO_STATIONS.map(({ id, name }) => [id, name]));

const downloadEvents = (events) => {
  const keys = ['id', 'timestamp', 'stationId', 'productId', 'shift', 'defectName', 'status', 'severity', 'confidence'];
  const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const content = [keys.join(','), ...events.map((event) => keys.map((key) => escapeCsv(event[key])).join(','))].join('\r\n');
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'perception-quality-events.csv';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const DemoEventsTable = ({ events, title = 'Recent quality events', maxRows = 10, controls = true }) => {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  const filtered = useMemo(() => events.filter((event) => {
    const matchesStatus = status === 'all' || event.status === status;
    const search = query.trim().toLowerCase();
    const matchesQuery = !search || [event.id, event.defectName, event.stationId, productNames[event.productId]]
      .some((field) => field.toLowerCase().includes(search));
    return matchesStatus && matchesQuery;
  }), [events, query, status]);

  const pages = Math.max(1, Math.ceil(filtered.length / maxRows));
  const currentPage = Math.min(page, pages);
  const visible = filtered.slice((currentPage - 1) * maxRows, currentPage * maxRows);
  const handleStatus = (value) => { setStatus(value); setPage(1); setExpandedId(null); };
  const handleQuery = (value) => { setQuery(value); setPage(1); setExpandedId(null); };

  return (
    <DemoCard title={title} right={`${filtered.length} records`}>
      {controls && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[170px] flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 size-4 text-perception-gray-600"/>
            <input value={query} onChange={(event) => handleQuery(event.target.value)} placeholder="Search events..." className="w-full rounded-md border border-perception-gray-700 bg-perception-black-700 py-2 pl-9 pr-3 text-xs text-perception-gray-100 outline-none focus:border-perception-blue"/>
          </div>
          <select value={status} onChange={(event) => handleStatus(event.target.value)} className="rounded-md border border-perception-gray-700 bg-perception-black-700 px-3 py-2 text-xs text-perception-gray-100 outline-none focus:border-perception-blue">
            <option value="all">All decisions</option>
            <option value="Corrected">Corrected</option>
            <option value="Rejected">Rejected</option>
            <option value="Dismissed">Dismissed</option>
          </select>
          <button type="button" onClick={() => downloadEvents(filtered)} className="flex items-center gap-2 rounded-md bg-perception-black-700 px-3 py-2 text-xs text-perception-gray-300 transition-colors hover:bg-perception-gray-800"><ArrowDownTrayIcon className="size-4"/> Export CSV</button>
        </div>
      )}
      <div className="overflow-x-auto rounded-md border border-perception-black-700">
        <table className="w-full min-w-[760px] text-left">
          <thead className="bg-perception-black-700">
            <tr className="text-xs font-semibold uppercase text-perception-gray-400">
              <th className="w-8 px-3 py-3"/>
              <th className="px-3 py-3">Time</th>
              <th className="px-3 py-3">Workstation</th>
              <th className="px-3 py-3">Reference</th>
              <th className="px-3 py-3">Deviation</th>
              <th className="px-3 py-3">Decision</th>
              <th className="px-3 py-3 text-right">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((event) => (
              <Fragment key={event.id}>
                <tr onClick={() => setExpandedId((value) => value === event.id ? null : event.id)} className="cursor-pointer border-t border-perception-black-700 text-xs text-perception-gray-300 transition-colors hover:bg-perception-black-700/70">
                  <td className="px-3 py-3 text-perception-gray-600">{expandedId === event.id ? <ChevronDownIcon className="size-4"/> : <ChevronRightIcon className="size-4"/>}</td>
                  <td className="whitespace-nowrap px-3 py-3">{formatDemoTimestamp(event.timestamp)}</td>
                  <td className="px-3 py-3">{stationNames[event.stationId]}</td>
                  <td className="whitespace-nowrap px-3 py-3">{productNames[event.productId]}</td>
                  <td className="px-3 py-3">{event.defectName}</td>
                  <td className="px-3 py-3"><DemoStatus status={event.status}/></td>
                  <td className="px-3 py-3 text-right">{formatDemoPercent(event.confidence * 100, 1)}</td>
                </tr>
                {expandedId === event.id && (
                  <tr className="border-t border-perception-black-700 bg-perception-black-800/70">
                    <td colSpan={7} className="px-6 py-4">
                      <div className="grid grid-cols-2 gap-4 text-xs md:grid-cols-4">
                        <div><div className="text-perception-gray-600">Event ID</div><div className="mt-1 text-perception-gray-300">{event.id}</div></div>
                        <div><div className="text-perception-gray-600">Production shift</div><div className="mt-1 text-perception-gray-300">Shift {event.shift}</div></div>
                        <div><div className="text-perception-gray-600">Severity</div><div className="mt-1 text-perception-gray-300">{event.severity}</div></div>
                        <div><div className="text-perception-gray-600">Resolution</div><div className="mt-1 text-perception-gray-300">{event.status === 'Corrected' ? 'Recovered before final rejection' : event.status === 'Rejected' ? 'Removed from production flow' : 'Alert dismissed after verification'}</div></div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {visible.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-perception-gray-600">No events match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="mt-3 flex items-center justify-end gap-3 text-xs text-perception-gray-600">
          <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={currentPage <= 1} className="rounded-md bg-perception-black-700 px-3 py-2 text-perception-gray-300 disabled:opacity-40">Previous</button>
          <span>Page {currentPage} / {pages}</span>
          <button type="button" onClick={() => setPage((current) => Math.min(pages, current + 1))} disabled={currentPage >= pages} className="rounded-md bg-perception-black-700 px-3 py-2 text-perception-gray-300 disabled:opacity-40">Next</button>
        </div>
      )}
    </DemoCard>
  );
};
